import { Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload, FiFile, FiFileText } from "react-icons/fi";
import { bffApi, type PortalSourcingEventDetailDto, useGetPortalSupplierInformationQuery } from "../../../../autogen/bff-api";
import { collectPublishedDocuments } from "../../../../common/components/communication/publishedDocuments";
import { useDocumentCategoryLabel } from "../../../../common/documents/useDocumentCategoryLabel";
import { useApiError } from "../../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch, useAppSelector } from "../../../../common/redux/hooks";
import { formatShortDate } from "../utils/displayFormatters";
import { SectionCard } from "./SectionCard";

export const EventDocumentsSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const documentCategoryLabel = useDocumentCategoryLabel();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const documents = event.documents ?? [];
  const textDocuments = event.textDocuments ?? [];
  const locale = momentLocaleFor(i18n.language);

  const authState = useAppSelector((state) => state.authState.state);
  const isLoggedIn = authState?.type === "LoggedInWithOrgContext";
  const { data: announcementsData } = useGetPortalSupplierInformationQuery({ eventId: event.id }, { skip: !isLoggedIn });
  const publishedDocuments = collectPublishedDocuments(
    announcementsData?.supplierInformation ?? [],
    documents.map((d) => d.id),
  );

  // Competition documents download via the public no-auth route; documents the buyer published to all tenderers
  // are access-restricted, so they go through the authenticated Q&A-attachment route. Both render as one list.
  const fileRows = [
    ...documents.map((d) => ({
      id: d.id,
      name: d.name,
      subtitle: [d.category ? documentCategoryLabel(d.category) : null, formatShortDate(d.uploadedAt, locale)]
        .filter(Boolean)
        .join(" · "),
      restricted: false,
    })),
    ...publishedDocuments.map((d) => ({
      id: d.id,
      name: d.name,
      subtitle: formatShortDate(d.uploadedAt, locale),
      restricted: true,
    })),
  ];

  if (fileRows.length === 0 && textDocuments.length === 0) return null;

  const handleDownload = async (documentId: string, restricted: boolean) => {
    setPendingId(documentId);
    try {
      const result = restricted
        ? await dispatch(
            bffApi.endpoints.getSeV2AttachmentDownloadLink.initiate({ eventId: event.id, documentId })
          ).unwrap()
        : await dispatch(
            bffApi.endpoints.getPortalDocumentDownloadLink.initiate({ eventId: event.id, documentId })
          ).unwrap();
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      displayer.trigger(error);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <SectionCard icon={FiDownload} title={t("Procurement documents")} count={fileRows.length + textDocuments.length}>
      <Stack spacing="4">
        {fileRows.map((doc) => (
          <HStack
            key={doc.id}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
            bg="smBackground"
            justify="space-between"
            align="center"
            spacing="3"
          >
            <HStack spacing="3" align="center" flex="1" minW="0">
              <Box color="smMuted" flexShrink="0">
                <FiFile size={18} />
              </Box>
              <Stack spacing="0" minW="0">
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {doc.name}
                </Text>
                <Text fontSize="xs" color="smMuted">
                  {doc.subtitle}
                </Text>
              </Stack>
            </HStack>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              leftIcon={<FiDownload />}
              onClick={() => handleDownload(doc.id, doc.restricted)}
              isLoading={pendingId === doc.id}
              flexShrink="0"
            >
              {t("Download")}
            </Button>
          </HStack>
        ))}

        {textDocuments.map((doc) => (
          <Box key={doc.id} borderWidth="1px" borderColor="smBorder" rounded="md" px="4" py="3" bg="smBackground">
            <HStack spacing="3" align="center" mb="3">
              <Box color="smMuted" flexShrink="0">
                <FiFileText size={18} />
              </Box>
              <Stack spacing="0" minW="0">
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {doc.name || t("Untitled document")}
                </Text>
                <Text fontSize="xs" color="smMuted">
                  {formatShortDate(doc.createdAt, locale)}
                </Text>
              </Stack>
            </HStack>
            <TipTap editable={false} content={doc.text} hasBorder={false} padding="0" maxHeight="none" />
          </Box>
        ))}
      </Stack>
    </SectionCard>
  );
};
